// BusinessActivityBar.jsx - Desktop component with progress bars for all business activities
import { useNavigate } from "react-router-dom";
import ThreadSahiIcon from "../../../Components/Common/ThreadSahiIcon";
import { useCurrency } from "../../../Hooks/useCurrency";
import { ChevronUp, ChevronDown, Plus, SquareActivity } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import CustomTooltip from "../../../Components/Common/CustomTooltip";
import ThreadDropdown from "../../Threads/AddThreadDropdown";
import { checkUserRights, currentUserHasPermission } from "../../../Utils/Utilities";
import { getConfigData } from "../../../Utils/Storage";

const BusinessActivityBar = ({
  businessActivities = [],
  threadData = [],
  onThreadEntryOpen = () => { }
}) => {
  const navigate = useNavigate();
  const currencySymbol = useCurrency();
  const [expandedSections, setExpandedSections] = useState({});
  const dropdownRef = useRef(null);
  const thrActions = currentUserHasPermission('ThrActions');
  const canAddThr = thrActions >= 1 && thrActions <= 3;
  const userRights = checkUserRights();

  // Divide business activities into two groups
  const salesActivities = businessActivities.filter(activity => activity && [1, 2, 3].includes(activity.id));
  const purchaseActivities = businessActivities.filter(activity => activity && [4, 5, 6].includes(activity.id));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setExpandedSections({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleSection = (activityId) => {
    setExpandedSections(prev => ({
      ...prev,
      [activityId]: !prev[activityId]
    }));
  };

  // Get data from localStorage
  const getThreadStages = () => {
    try {
      const configData = getConfigData();
      const threadStages = JSON.parse(configData?.ThreadStages || '[]');
      return threadStages;
    } catch (error) {
      console.error('Error parsing thread stages:', error);
      return [];
    }
  };

  // Get real thread data for specific activity type
  const getThreadDataForType = (typeId) => {
    return threadData.filter(thread => thread.TypeID === typeId && thread.StatusID != 3);
  };

  // Calculate stage-wise breakdown
  const getStageBreakdown = (threads, stages) => {
    const stageBreakdown = {};

    stages.forEach(stage => {
      stageBreakdown[stage.id] = {
        name: stage.name,
        count: 0,
        value: 0,
        stageIndex: stage.stageIndex // Keep track of original stage position
      };
    });

    threads.forEach(thread => {
      const currentStageId = thread.CurrStageID;
      if (stageBreakdown[currentStageId]) {
        stageBreakdown[currentStageId].count += 1;
        stageBreakdown[currentStageId].value += parseFloat(thread.BizProspect || 0);
      }
    });

    return Object.values(stageBreakdown);
  };

  // Get stage color based on stage index and activity type
  const getStageColor = (stageIndex, activityId) => {
    if (activityId === 1 || activityId === 4) {

      const orangeColors = ['#f49966', '#e26f1f', '#bf5700', '#9a4000', '#7a3400'];
      return orangeColors[stageIndex] || '#f97316';
    } else if (activityId === 2 || activityId === 5) {

      const greenColors = ['#63d68c', '#2ea447', '#0b5d1e', '#073613', '#041f0b'];
      return greenColors[stageIndex] || '#22c55e';
    } else if (activityId === 3 || activityId === 6) {

      const blueColors = ['#4db3d1', '#2fa3c7', '#0c6980', '#094f66', '#06394d'];
      return blueColors[stageIndex] || '#0c6980';
    }

    return '#6b7280';
  };

  // Function which will calculate activity details
  const getActivityDetails = (activity) => {
    const threadStages = getThreadStages();
    const activityStages = threadStages.find(thread => thread.TID === activity.id);

    // Get stages for this activity type with their original index
    const stages = activityStages?.Stages?.map((stage, index) => ({
      id: stage.SID,
      name: stage.S,
      stageIndex: index // Keep track of original position
    })) || [];

    // Get threads for this activity type
    const threadsForType = getThreadDataForType(activity.id);

    // Calculate stage-wise breakdown
    const stageBreakdown = getStageBreakdown(threadsForType, stages);

    // Calculate totals
    const totalValue = stageBreakdown.reduce((sum, stage) => sum + stage.value, 0);
    const totalCount = stageBreakdown.reduce((sum, stage) => sum + stage.count, 0);

    // Create stage bars data for individual divs
    const getStageSegments = (stages) => {
      const total = stages.reduce((sum, stage) => sum + stage.count, 0);

      if (total === 0) {
        // When no data, show all stages with equal width
        return stages.map(stage => ({
          ...stage,
          widthPercentage: 100 / stages.length, // Equal width for all stages
          color: getStageColor(stage.stageIndex, activity.id)
        }));
      }

      // When there is data, only show stages with actual counts
      const stagesWithData = stages.filter(stage => stage.count > 0);
      return stagesWithData.map(stage => ({
        ...stage,
        widthPercentage: total > 0 ? (stage.count / total) * 100 : 0,
        color: getStageColor(stage.stageIndex, activity.id)
      }));
    };

    return {
      totalValue,
      totalCount,
      stages: totalCount === 0 ? stageBreakdown : stageBreakdown.filter(stage => stage.count > 0 || stage.value > 0), // Show all stages only when no data
      stageSegments: getStageSegments(stageBreakdown) // Show segments based on data availability
    };
  };

  // Render activity section
  const renderActivitySection = (activities, sectionTitle, color, shouldOpenUpward, sectionClass = "") => {
    if (activities.length === 0) return null;

    return (
      <div className={`rounded-xl bg-bg-white border border-sahi-border-color shadow-md ${sectionClass}`}>
        {/* Header */}
        <div
          className="grid 4xl:p-4 p-[0.75rem] font-semibold flex-shrink-0 border-b border-sahi-border-color text-text-color"
          style={{ gridTemplateColumns: "32% 45% 17% 8%" }}
        >
          <div className="flex items-center whitespace-nowrap">
            <SquareActivity className={`h-5 w-5 mr-2 ${color}`} />
            <span className="text-lg truncate">{sectionTitle}</span>
          </div>
        </div>

        {/* Business Activity Rows */}
        <div>
          {activities.map((activity, index) => {
            if (!activity?.id) {
              return null; // Skip if no valid activity ID
            }
            const details = getActivityDetails(activity);
            const isExpanded = expandedSections[activity.id];
            const hasStages = details.stages && details.stages.length > 0;
            const isLastItem = index === activities.length - 1;
            // Determine if dropdown should open upward (for last 3 items)
            // const shouldOpenUpward = index >= activities.length - 3;
            return (
              <div
                key={activity.id}
                className={`relative transition-all duration-200 bg-bg-white ${index < activities.length - 1 ? "border-b border-sahi-border-color" : ""
                  } ${isLastItem ? "rounded-b-xl" : ""}`}
              >
                <div className="grid 4xl:p-4 p-[0.75rem] " style={{ gridTemplateColumns: "32% 45% 17% 8%", minHeight: '60px' }}>
                  {/* Activity Name and Icon */}
                  <div
                    className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
                    onClick={() => navigate(`/threads?tid=${activity.id}`)}
                  >
                    <ThreadSahiIcon tID={activity.id} className="w-5 h-5" />
                    <span className="text-lg text-text-color font-medium truncate">{activity.name}</span>
                  </div>

                  {/* Progress Bar Section */}
                  <div className="flex items-center px-4">
                    {hasStages ? (
                      <div className="w-full flex items-center gap-2">
                        <div className="flex-1 h-[20px] overflow-hidden flex rounded-lg">
                          {details.stageSegments
                            // .sort((a, b) => b.stageIndex - a.stageIndex) // Sort in reverse order
                            .map((segment, segmentIndex) => (
                              <CustomTooltip
                                key={segment.stageIndex}
                                content={(data) => (
                                  <div className="space-y-2 pb-3"
                                  >
                                    <div className="flex items-center gap-2 p-3 bg-bg-grey">
                                      <div
                                        className="w-2.5 h-2.5 rounded-full"
                                        style={{ backgroundColor: data.color }}
                                      />
                                      <h4 className="text-sm font-semibold text-sahi-primary-color">{data.name}</h4>
                                    </div>
                                    <div className="space-y-1.5 text-xs px-3">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">{[1, 2, 3].includes(activity.id) ? 'Revenue:' : 'Value:'}</span>
                                        <span className="font-medium text-gray-800">
                                          <span className="font-normal text-sahi-grey mr-1">
                                            {currencySymbol}
                                          </span>
                                          {data.value.toLocaleString()}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">{[1, 2, 3].includes(activity.id) ? 'Deals:' : 'Count:'}</span>
                                        <span className="font-medium text-gray-800">{data.count}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">{[1, 2, 3].includes(activity.id) ? 'Avg. Deal Size:' : 'Avg. Value:'}</span>
                                        <span className="font-medium text-gray-800">
                                          <span className="font-normal text-sahi-grey mr-1">
                                            {currencySymbol}
                                          </span>
                                          {data.count > 0 ? Math.round(data.value / data.count).toLocaleString() : '0'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              >
                                {({ onMouseEnter, onMouseLeave }) => (
                                  <div
                                    className="h-full cursor-pointer transition-opacity hover:opacity-80"
                                    style={{
                                      width: `${segment.widthPercentage}%`,
                                      backgroundColor: segment.color,
                                      borderTopLeftRadius: segmentIndex === 0 ? '0.5rem' : '0',
                                      borderBottomLeftRadius: segmentIndex === 0 ? '0.5rem' : '0',
                                      borderTopRightRadius: segmentIndex === details.stageSegments.length - 1 ? '0.5rem' : '0',
                                      borderBottomRightRadius: segmentIndex === details.stageSegments.length - 1 ? '0.5rem' : '0'
                                    }}
                                    onMouseEnter={(e) => onMouseEnter(segment, e)}
                                    onMouseLeave={onMouseLeave}
                                    onClick={() => navigate(`/threads?tid=${activity.id}&stg=${segment.stageIndex + 1}`)}
                                  />
                                )}
                              </CustomTooltip>
                            ))}
                        </div>
                         {/* Total Count Display */}
                      {/* <span className="text-gray-600 font-medium">
                        ({details.totalCount})
                      </span> */}
                        {hasStages && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSection(activity.id);
                            }}
                            className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                          >
                            {isExpanded ? <ChevronUp strokeWidth={3} className="h-5 w-5" /> : <ChevronDown strokeWidth={3} className="h-5 w-5" />}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="w-full flex items-center gap-2">
                        <div className="flex-1 h-[20px] bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-xs text-gray-500">No pipeline data</span>
                        </div>
                        {/* Show (0) for no data case */}
                        <span className="text-sm text-gray-600 font-medium">
                          (0)
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Value */}
                  <div className="text-right flex items-center justify-end">
                    <span className="text-xl font-bold">
                      <span className="text-base font-semibold mr-1 text-sahi-grey">{currencySymbol}</span>
                      {hasStages ? details.totalValue.toLocaleString() : activity.value}
                      <span className="text-gray-600 font-medium text-base ml-1">
                        ({details.totalCount})
                      </span>
                    </span>
                  </div>
                  {canAddThr &&
                    <div className="flex items-center justify-end pr-4">
                      <div
                        className="p-1 text-sahi-primary-color hover:cursor-pointer hover:bg-sahi-blue-light rounded duration-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          onThreadEntryOpen(activity.id);
                        }}
                      >
                        <Plus className="h-5 w-5" />
                      </div>
                    </div>
                  }</div>

                {/* Expanded Stage Details - Smart Positioning Overlay - Compact */}
                {
                  hasStages && isExpanded && (
                    <div
                      ref={dropdownRef}
                      className={`absolute left-0 right-0 z-50 bg-bg-grey border border-sahi-border-color rounded-md shadow-md p-2 ${shouldOpenUpward ? 'bottom-[50px] mb-1' : 'top-[50px] mt-1'
                        }`}
                      style={{ marginLeft: '35%', marginRight: '27%' }}
                    >
                      <div className="p-2 space-y-3">
                        {details.stages
                          .sort((a, b) => b.stageIndex - a.stageIndex) // Sort in reverse order (later stages first)
                          .map((stage) => (
                            <div key={stage.stageIndex} className="flex justify-between items-center p-3 bg-sahi-white rounded-md cursor-pointer"
                              onClick={() => navigate(`/threads?tid=${activity.id}&stg=${stage.stageIndex + 1}`)}>
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{
                                    backgroundColor: getStageColor(stage.stageIndex, activity.id)
                                  }}
                                />
                                <span className="text-sm font-medium text-gray-700">{stage.name}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-sm font-semibold text-gray-800">
                                  <span className="text-sahi-grey mr-1 font-normal">
                                    {currencySymbol}
                                  </span>
                                  {stage.value.toLocaleString()}
                                </span>
                                <span className="text-sm text-gray-500">({stage.count})</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )
                }
              </div>
            );
          })}
        </div>
      </div>
    );
  };  

  return (
       <div className="4xl:space-y-6 space-y-4">
      {userRights?.canSeeCstrs && salesActivities.length > 0 &&
        /* Sales Activities Section (IDs 1, 2, 3) */
        renderActivitySection(
          salesActivities,
          "Opportunities",
          "text-sahi-green",
          false
        )}
      {/* Suppliers - Desktop */}
      {userRights?.canSeeSplrs && purchaseActivities.length > 0 &&
        /* Purchase Activities Section (IDs 4, 5, 6) */
        renderActivitySection(
          purchaseActivities,
          "Supply Chain",
          "text-sahi-orange",
          salesActivities.length  ? true : false
        )}
    </div>
  );
};

export default BusinessActivityBar;